// Zero-knowledge identity backup envelope (cross-device key export/restore).
//
// THIS IS A SHARED, VERSIONED, BYTE-EXACT CONTRACT — the same discipline as
// `shared/crypto.ts`'s canonicalisers. It is imported by the browser SPA
// (`src/main.ts`) and hand-ported into the Android `wire/` package; a fixed
// salt+nonce parity vector (`canonical-vectors.json`, regenerated via
// `tools/gen-vectors.mjs` in the locus-android submodule) proves the two
// clients encrypt to the identical ciphertext and can cross-decrypt. Treat any
// change here like a `shared/crypto.ts` change: regenerate the vector, re-port
// `wire/`. See ADR 0016.
//
// The server NEVER imports this module: it stores the serialized envelope as
// opaque columns and only validates field shapes. The plaintext seed and the
// passphrase never leave the client — the server holds ciphertext only.
//
// Runtime-neutral: only TextEncoder / JSON / `crypto.subtle`, identical in the
// browser and the Workers runtime.

import { b64urlEncode, b64urlDecode } from './crypto';
import { isValidLabel, isValidGroupKey } from './token';

export const BACKUP_VERSION = 1;
// The AAD bound into the GCM tag. Stripping/downgrading the version field then
// fails the integrity check rather than silently decrypting under a new format.
export const BACKUP_VERSION_TAG = 'locus-backup-v1';
export const KDF_NAME = 'pbkdf2-sha256';
// OWASP 2023 PBKDF2-HMAC-SHA256 floor. Carried in the envelope so it can be
// raised later without breaking older backups. PBKDF2 (not Argon2) because it
// is the only KDF native to BOTH WebCrypto and the Android runtime (ADR 0016).
export const KDF_ITERATIONS_DEFAULT = 600_000;
export const KDF_ITERATIONS_MIN = 100_000;
export const KDF_ITERATIONS_MAX = 10_000_000;
export const AEAD_NAME = 'aes-256-gcm';

export const SALT_LEN = 16;
export const NONCE_LEN = 12;
export const SEED_LEN = 32;

// pkcs8 ed25519 = a fixed 16-byte prefix + the 32-byte raw seed (RFC 8410, the
// same prefix `tools/gen-vectors.mjs` uses). The browser holds the private key
// as pkcs8 (ADR 0003); Android holds the raw seed. The envelope standardises on
// the RAW seed so both clients are byte-identical, and the web side converts.
const PKCS8_ED25519_PREFIX = new Uint8Array([
  0x30, 0x2e, 0x02, 0x01, 0x00, 0x30, 0x05, 0x06, 0x03, 0x2b, 0x65, 0x70, 0x04, 0x22, 0x04, 0x20,
]);

/** The plaintext identity the envelope protects. `seed` is the RAW 32-byte
 *  ed25519 seed; `group` is `[]` (ungrouped) or exactly 2 segments. */
export interface IdentityBundle {
  label: string[];
  group: string[];
  seed: Uint8Array;
  // Client clock when the identity was created. Defaults to Date.now() at
  // encrypt time. Lives INSIDE the ciphertext (distinct from the server row's
  // created_at/updated_at columns).
  created_at?: number;
}

/** The serialized artifact `POST /backups` carries. Every field is opaque to
 *  the server: it stores them columnar and never parses the ciphertext. */
export interface BackupEnvelope {
  version: number;
  kdf: string;
  kdf_iterations: number;
  kdf_salt: string; // b64url(16B)
  aead: string;
  aead_nonce: string; // b64url(12B)
  ciphertext: string; // b64url(AES-GCM output, incl. the appended 16-byte tag)
  ref: string; // the device token — the per-device backup handle
  label_hint: string | null; // optional, non-sensitive friendly name
}

export interface RestoredIdentity {
  label: string[];
  group: string[];
  seed: Uint8Array; // raw 32-byte seed
  created_at: number;
}

export interface EncryptOptions {
  ref: string;
  label_hint?: string | null;
  iterations?: number;
  // Deterministic hooks for the parity vector / tests. Production callers omit
  // them and a fresh CSPRNG salt + nonce are generated per backup.
  salt?: Uint8Array;
  nonce?: Uint8Array;
}

/* ── pkcs8 ↔ raw seed (web only; Android already holds the raw seed) ──────── */

export function pkcs8ToRawSeed(pkcs8: Uint8Array): Uint8Array {
  if (pkcs8.length !== PKCS8_ED25519_PREFIX.length + SEED_LEN) {
    throw new Error('backup: unexpected pkcs8 length');
  }
  for (let i = 0; i < PKCS8_ED25519_PREFIX.length; i++) {
    if (pkcs8[i] !== PKCS8_ED25519_PREFIX[i]) throw new Error('backup: not an ed25519 pkcs8 key');
  }
  return pkcs8.slice(PKCS8_ED25519_PREFIX.length);
}

export function rawSeedToPkcs8(seed: Uint8Array): Uint8Array {
  if (seed.length !== SEED_LEN) throw new Error('backup: seed must be 32 bytes');
  const out = new Uint8Array(PKCS8_ED25519_PREFIX.length + SEED_LEN);
  out.set(PKCS8_ED25519_PREFIX, 0);
  out.set(seed, PKCS8_ED25519_PREFIX.length);
  return out;
}

// Recover the raw 32-byte ed25519 PUBLIC key from a raw seed using only
// WebCrypto — no external curve code, no new dependency. Trick: import the
// rebuilt pkcs8 as an *extractable* key, then read the JWK `x` (the public
// point the runtime derives). The browser needs this on restore because its
// keypair store wants {priv, pub} but the envelope carries only the seed.
export async function publicKeyFromSeed(seed: Uint8Array): Promise<Uint8Array> {
  const pkcs8 = rawSeedToPkcs8(seed);
  const key = await crypto.subtle.importKey('pkcs8', pkcs8 as BufferSource, { name: 'Ed25519' }, true, [
    'sign',
  ]);
  const jwk = (await crypto.subtle.exportKey('jwk', key)) as JsonWebKey;
  if (typeof jwk.x !== 'string') throw new Error('backup: could not derive public key');
  return b64urlDecode(jwk.x);
}

/* ── envelope ─────────────────────────────────────────────────────────────── */

// Plaintext JSON, exact key order: version, label, group, seed, created_at.
// No whitespace, no replacer — the cross-client byte contract.
function encodePlaintext(label: string[], group: string[], seed: Uint8Array, created_at: number): Uint8Array {
  const canonical = {
    version: BACKUP_VERSION,
    label,
    group,
    seed: b64urlEncode(seed),
    created_at,
  };
  return new TextEncoder().encode(JSON.stringify(canonical));
}

async function deriveAesKey(passphrase: string, salt: Uint8Array, iterations: number): Promise<CryptoKey> {
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(passphrase) as BufferSource,
    'PBKDF2',
    false,
    ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: salt as BufferSource, iterations, hash: 'SHA-256' },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

export async function encryptBackup(
  passphrase: string,
  bundle: IdentityBundle,
  opts: EncryptOptions,
): Promise<BackupEnvelope> {
  if (typeof passphrase !== 'string' || passphrase.length === 0) {
    throw new Error('backup: passphrase required');
  }
  if (bundle.seed.length !== SEED_LEN) throw new Error('backup: seed must be 32 bytes');
  if (!isValidLabel(bundle.label)) throw new Error('backup: invalid label');
  const group = Array.isArray(bundle.group) ? bundle.group : [];
  if (!isValidGroupKey(group)) throw new Error('backup: invalid group');

  const iterations = opts.iterations ?? KDF_ITERATIONS_DEFAULT;
  const salt = opts.salt ?? crypto.getRandomValues(new Uint8Array(SALT_LEN));
  const nonce = opts.nonce ?? crypto.getRandomValues(new Uint8Array(NONCE_LEN));
  if (salt.length !== SALT_LEN) throw new Error('backup: salt must be 16 bytes');
  if (nonce.length !== NONCE_LEN) throw new Error('backup: nonce must be 12 bytes');
  const created_at = bundle.created_at ?? Date.now();

  const aesKey = await deriveAesKey(passphrase, salt, iterations);
  const plaintext = encodePlaintext(bundle.label, group, bundle.seed, created_at);
  const aad = new TextEncoder().encode(BACKUP_VERSION_TAG);
  const ct = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: nonce as BufferSource, additionalData: aad as BufferSource },
    aesKey,
    plaintext as BufferSource,
  );

  return {
    version: BACKUP_VERSION,
    kdf: KDF_NAME,
    kdf_iterations: iterations,
    kdf_salt: b64urlEncode(salt),
    aead: AEAD_NAME,
    aead_nonce: b64urlEncode(nonce),
    ciphertext: b64urlEncode(new Uint8Array(ct)),
    ref: opts.ref,
    label_hint: opts.label_hint ?? null,
  };
}

export async function decryptBackup(passphrase: string, env: BackupEnvelope): Promise<RestoredIdentity> {
  if (env.version !== BACKUP_VERSION) throw new Error('backup: unsupported version');
  if (env.kdf !== KDF_NAME) throw new Error('backup: unsupported kdf');
  if (env.aead !== AEAD_NAME) throw new Error('backup: unsupported aead');
  const iterations = env.kdf_iterations;
  if (!Number.isInteger(iterations) || iterations < KDF_ITERATIONS_MIN || iterations > KDF_ITERATIONS_MAX) {
    throw new Error('backup: iteration count out of bounds');
  }
  const salt = b64urlDecode(env.kdf_salt);
  const nonce = b64urlDecode(env.aead_nonce);
  if (salt.length !== SALT_LEN) throw new Error('backup: bad salt length');
  if (nonce.length !== NONCE_LEN) throw new Error('backup: bad nonce length');

  const aesKey = await deriveAesKey(passphrase, salt, iterations);
  const aad = new TextEncoder().encode(BACKUP_VERSION_TAG);
  let ptBuf: ArrayBuffer;
  try {
    ptBuf = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: nonce as BufferSource, additionalData: aad as BufferSource },
      aesKey,
      b64urlDecode(env.ciphertext) as BufferSource,
    );
  } catch {
    throw new Error('backup: decryption failed (wrong passphrase or corrupt backup)');
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(new TextDecoder().decode(new Uint8Array(ptBuf)));
  } catch {
    throw new Error('backup: decrypted payload is not valid JSON');
  }
  if (typeof parsed !== 'object' || parsed === null) throw new Error('backup: payload not an object');
  const p = parsed as Record<string, unknown>;
  if (p.version !== BACKUP_VERSION) throw new Error('backup: payload version mismatch');
  if (!isValidLabel(p.label)) throw new Error('backup: payload label invalid');
  if (!isValidGroupKey(p.group)) throw new Error('backup: payload group invalid');
  if (typeof p.seed !== 'string') throw new Error('backup: payload seed missing');
  const seed = b64urlDecode(p.seed);
  if (seed.length !== SEED_LEN) throw new Error('backup: payload seed wrong length');

  return {
    label: p.label,
    group: Array.isArray(p.group) ? (p.group as string[]) : [],
    seed,
    created_at: typeof p.created_at === 'number' ? p.created_at : 0,
  };
}
